import { useState, type ReactNode } from 'react';
import { useFeatureTranslation } from '@/hooks/useFeatureTranslation';

/** Rows a card shows before the rest fold behind a toggle. */
export const COLLAPSED_ROWS = 20;

interface Props<TRow> {
  rows: readonly TRow[];
  renderRow: (row: TRow) => ReactNode;
  /** Wraps the visible rows, e.g. a `ul` or `dl`. */
  container: (children: ReactNode) => ReactNode;
}

/** Every row of a card, the first 20 shown and the rest behind a "show all" toggle. */
export function CollapsibleRows<TRow>({ rows, renderRow, container }: Props<TRow>) {
  const t = useFeatureTranslation('aiChat');
  const [expanded, setExpanded] = useState(false);
  const foldable = rows.length > COLLAPSED_ROWS;
  const visible = foldable && !expanded ? rows.slice(0, COLLAPSED_ROWS) : rows;

  return (
    <>
      {container(visible.map(renderRow))}
      {foldable && (
        <button
          type='button'
          className='self-start text-sm text-primary-base underline'
          aria-expanded={expanded}
          onClick={() => setExpanded(value => !value)}
        >
          {expanded
            ? t('proposals.rows.showLess')
            : t('proposals.rows.showAll', { count: rows.length })}
        </button>
      )}
    </>
  );
}
